import type {
	AccreditationListPage,
	AccreditationTravelFilter,
	Flight,
	FlightOverrideBody,
	TravelBulkBody,
	TravelBulkResult,
	TravelDelayBody,
	TravelDelayResult,
	TravelDirection,
	TravelMode,
	TravelSummary,
} from "@seagames/contracts";
import { type InfiniteData, infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { accreditationKeys } from "./accreditations";
import { api } from "./client";
import { invalidateLogistics } from "./logistics-cache";

/**
 * Travel (M9): the summary read (the journeys reads are in
 * `travel-journeys.ts`) and the A&D writes (bulk add, flight override, resume
 * tracking). Wire shapes are S0's contracts (`travel.ts`); every read is
 * ONE request the API answers with one statement (PLAN §3).
 */

/** A range filter in WIRE form (the old `/travel/groups` shape; `GroupDelay` still takes it). `status` is the comma list. */
export interface TravelRangeFilter {
	games_id: string;
	direction: TravelDirection;
	from: string;
	to?: string;
	mode?: TravelMode;
	entry_point_id?: string;
	responsible_organization_id?: string;
	status?: string;
	q?: string;
}

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

export const travelKeys = {
	all: ["travel"] as const,
	summary: (gamesId: string, date: string | undefined) => ["travel", "summary", gamesId, date ?? null] as const,
};

/** `GET /travel/summary` — the Overview tiles and the hourly load, one aggregate. `date` defaults to today (games' zone). */
export const travelSummaryOptions = (gamesId: string, date?: string) =>
	queryOptions({
		queryKey: travelKeys.summary(gamesId, date),
		queryFn: ({ signal }) => api.get<TravelSummary>("/travel/summary", { query: clean({ games_id: gamesId, date }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/**
 * After a travel write: every read of the travel chain (`invalidateLogistics`) — A&D, the transport
 * suggestions, the rooms waiting, the Contingent Dashboard and the records (their travel section and
 * the `travel=` filter). A new or moved leg is a new suggestion and a new "needs a room" row.
 */
export const invalidateTravel = invalidateLogistics;

// ── Writes ──────────────────────────────────────────────────────────────────

/** `POST /travel/bulk` — the same leg for up to TRAVEL_BULK_MAX cards; refusals per card, never a throw. */
export const bulkTravel = (body: TravelBulkBody) => api.post<TravelBulkResult>("/travel/bulk", body);

/** `PATCH /flights/:id` (host desk) — sets the flight's times by hand; tracking pauses until resumed. */
export const overrideFlight = (flightId: string, body: FlightOverrideBody) => api.patch<Flight>(`/flights/${flightId}`, body);

/** `POST /travel/delay` — set (or with `null` clear) the manual delay of legs with no flight row; refusals per leg. */
export const delayTravel = (body: TravelDelayBody) => api.post<TravelDelayResult>("/travel/delay", body);

/** `POST /flights/:id/resume-tracking` — hand the flight back to the provider. */
export const resumeFlightTracking = (flightId: string) => api.post<Flight>(`/flights/${flightId}/resume-tracking`);

// ── The records picker ("Add travel") ───────────────────────────────────────

export interface TravelPickerFilter {
	games_id: string;
	/** Comma list of card statuses. */
	status?: string;
	responsible_organization_id?: string;
	/** Only cards still without this leg. */
	travel?: AccreditationTravelFilter;
	q?: string;
}

const PICKER_PAGE = 50;

/**
 * `GET /accreditations` (the records list's own endpoint and scope), keyset,
 * for the "Add travel" picker. Under the accreditations key, so any record
 * write refreshes it.
 */
export const travelPickerOptions = (f: TravelPickerFilter) =>
	infiniteQueryOptions<AccreditationListPage, Error, InfiniteData<AccreditationListPage>, readonly unknown[], string | undefined>({
		queryKey: [...accreditationKeys.all, "travel-picker", clean(f)],
		queryFn: ({ signal, pageParam }) =>
			api.get<AccreditationListPage>("/accreditations", { query: { ...clean(f), sort: "name_asc", limit: PICKER_PAGE, cursor: pageParam }, signal }),
		initialPageParam: undefined,
		getNextPageParam: (last) => last.next_cursor ?? undefined,
		staleTime: 15_000,
	});

